import React, { useState } from 'react';
import Popper, { PopperProps } from '@material-ui/core/Popper';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Autocomplete, {
  AutocompleteProps,
  AutocompleteInputChangeReason
} from '@material-ui/lab/Autocomplete';
import { UseAutocompleteProps } from '@material-ui/lab/useAutocomplete';

const useStyles = makeStyles(theme => ({
  placeholder: { color: '#696969' },
  icon: {
    color: theme.palette.common.black,
    fontSize: theme.typography.body1.fontSize,
    lineHeight: theme.typography.pxToRem(15),
    top: 'unset'
  },
  input: {
    paddingTop: '0 !important',
    paddingBottom: '0 !important'
  },
  inputRoot: {
    paddingTop: '0 !important',
    paddingBottom: '0 !important'
  },
  popper: {
    width: 300,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  resultsText: {
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    paddingLeft: theme.spacing(2),
    backgroundColor: theme.palette.grey[100],
    fontStyle: 'italic'
  }
}));

interface ListboxProps extends React.PropsWithChildren<React.HTMLAttributes<HTMLElement>> {
  resultsText?: string;
}

const ListboxComponent: React.FC<ListboxProps> = ({ resultsText, ...rest }) => {
  const classes = useStyles();
  return (
    <>
      {resultsText && <Typography className={classes.resultsText}>{resultsText}</Typography>}
      <ul {...rest} />
    </>
  );
};

interface EntitySearchProps {
  resultsText?: string;
  limit?: number;
}

const EntitySearch = <T,>({
  loading = false,
  onInputChange,
  limit,
  resultsText,
  ...rest
}: AutocompleteProps<T> & UseAutocompleteProps<T> & EntitySearchProps): React.ReactElement => {
  const classes = useStyles();
  const [inputValue, setInputValue] = useState<string>('');
  const { options } = rest;

  const handleInputChange = (
    event: React.ChangeEvent<{}>,
    value: string,
    reason: AutocompleteInputChangeReason
  ): void => {
    setInputValue(value);
    onInputChange && onInputChange(event, value, reason);
  };

  const showResultsText = limit && resultsText && options.length >= limit;
  const OptionsPopper = (props: PopperProps): React.ReactElement => (
    <Popper {...props} style={{ width: 325 }} placement="bottom-start" />
  );

  return (
    <Autocomplete
      autoComplete
      size="small"
      loading={loading}
      inputValue={inputValue}
      popupIcon={<ExpandMoreIcon className={classes.icon} />}
      PopperComponent={OptionsPopper}
      ListboxComponent={(props): React.ReactElement => (
        <ListboxComponent {...props} resultsText={showResultsText ? resultsText : undefined} />
      )}
      onInputChange={handleInputChange}
      classes={{
        input: classes.inputRoot,
        inputRoot: classes.inputRoot
      }}
      {...rest}
    />
  );
};

export default EntitySearch;
